import { API_CONFIG } from "@/infrastructure/config/api";
import { delApi, getApi, patchApi, postApi } from "@/shared/utils/fetchApi";
import type {
  SkillCatalogDetail,
  SkillInstallationScope,
  SkillPublisherScope,
  SkillResourceContent,
} from "@/features/Skills/types/skill";

/**
 * Uma linha do catálogo na tela de superadministração (AB#85634, AC1).
 *
 * Não é a mesma coisa que a linha da vitrine: aqui vem a skill **inativa** e a
 * **core** — que a loja esconde, uma por estar fora do ar e a outra por não se
 * instalar — mais as contagens, que são o que diz a quem cura se uma decisão
 * atinge alguém.
 */
export interface SkillAdminEntry {
  name: string;
  title: string | null;
  description: string;
  /** Título e descrição por idioma; ausente cai no canônico (AB#85634, AC37). */
  title_i18n: Record<string, string> | null;
  description_i18n: Record<string, string> | null;
  /**
   * O DONO no Prodgy — quem responde pela skill aqui dentro (AB#85634).
   *
   * Nao e o `author`: aquele e quem escreveu o pacote e vem do `SKILL.md`. Uma
   * skill baixada da internet tem autor de fora e dono daqui.
   */
  owner_user_id: string | null;
  status: "active" | "inactive";
  inactive_reason: string | null;
  is_core: boolean;
  install_scope: SkillInstallationScope;
  facets: string[];
  provider: string | null;
  /** De onde o pacote veio, quando veio de fora (AB#85634). */
  source_url: string | null;
  author: string | null;
  version: number | null;
  version_count: number;
  installation_count: number;
  published_at: string | null;
}

/**
 * Administração do catálogo (AB#85634, AB#85637).
 *
 * A leitura e a curadoria — `list`, `update`, `publishVersion` — são do catálogo
 * da PLATAFORMA e só superadmin. Já o `publish` serve os três destinos: o
 * endpoint é um só e o destino é parâmetro, como no `skills_submit` do MCP.
 */
export class SkillsAdminService {
  private static base() {
    return `${API_CONFIG.API_ROOT_URL}/skills`;
  }

  static async list(): Promise<SkillAdminEntry[]> {
    const data = await getApi<{ skills: SkillAdminEntry[] }>(`${this.base()}/admin`);

    return data.skills ?? [];
  }

  /**
   * O detalhe de uma versão. Omitida, vale a corrente — informada, abre aquela,
   * inclusive uma que não está no ar.
   */
  static async detail(name: string, version?: number): Promise<SkillCatalogDetail> {
    const query = version === undefined ? "" : `?version=${version}`;

    return getApi<SkillCatalogDetail>(`${this.base()}/admin/${encodeURIComponent(name)}${query}`);
  }

  static async versions(name: string): Promise<SkillAdminVersion[]> {
    const data = await getApi<{ versions: SkillAdminVersion[] }>(
      `${this.base()}/admin/${encodeURIComponent(name)}/versions`,
    );

    return data.versions ?? [];
  }

  /**
   * Registra um pacote: skill nova, ou versão nova de uma que já existe.
   *
   * Submeter **coloca no ar**: a versão entra viva no catálogo de destino,
   * disponível para instalar. Não há estado intermediário a resolver depois.
   *
   * Estar no ar não é estar servindo: instalar continua sendo o ato que põe um
   * procedimento na frente de um agente.
   */
  static async publish(payload: {
    folder_name: string;
    /**
     * Para qual catálogo a skill vai. Ausente vale `platform`.
     *
     * Quem alcança o destino registra nele; a autoridade é resolvida no
     * servidor, nunca aqui — a tela só diz de onde a pessoa clicou.
     */
    publisher_scope?: SkillPublisherScope;
    files: Array<{ path: string; content: string; encoding: "utf8" | "base64" }>;
    install_scope: SkillInstallationScope;
    /** Se a skill nasce core — obrigatória para todas as organizações. */
    is_core?: boolean;
    facets?: string;
    changelog?: string;
    /**
     * Publica E instala no mesmo ato, no nível do destino (AB#85637).
     *
     * Só para a organização, e só quando quem importa pediu: importar,
     * publicar e instalar são três atos, e para quem já sabe o que está
     * trazendo os três viram cerimônia.
     */
    install?: boolean;
    /** Sobrescritas de vitrine. Omitidas, valem as do pacote. */
    title?: string;
    description?: string;
    /** As mesmas, por idioma — complemento do catálogo, nunca do pacote. */
    title_i18n?: Record<string, string>;
    description_i18n?: Record<string, string>;
    changelog_i18n?: Record<string, string>;
    /** Rótulo e ajuda dos parâmetros, e motivo dos requisitos, por idioma. */
    parameters_i18n?: Record<string, Record<string, { label?: string; help?: string }>>;
    integrations_i18n?: Record<string, Record<string, { reason?: string }>>;
    /** Quem responde pela skill no Prodgy; omitido, fica quem registrou. */
    owner_user_id?: string | null;
    /** De onde o pacote veio. Nulo apaga; ausente não mexe no que está gravado. */
    source_url?: string | null;
  }): Promise<{
    name: string;
    version: number;
    /**
     * O que impediu a instalação, quando o destino é o espaço pessoal.
     *
     * Só ali a importação instala no mesmo ato; a skill que declara integração
     * indisponível nasce registrada e NÃO instalada, e esta lista diz o que
     * falta. Vazia ou ausente, a instalação aconteceu.
     */
    unmet_requirements?: Array<{ requirement: string; reason: string }>;
  }> {
    return postApi(this.base(), payload);
  }

  /**
   * Muda o que é decisão do CATÁLOGO, e não do pacote (AB#85634).
   *
   * Um endpoint para os três campos, e não um por campo: `is_core`,
   * `install_scope` e a inativação respondem à mesma pergunta — o que a
   * plataforma decidiu sobre esta skill — e três rotas fariam o mesmo trabalho
   * em lugares diferentes.
   */
  static async update(
    name: string,
    payload: {
      is_core?: boolean;
      install_scope?: SkillInstallationScope;
      status?: "active" | "inactive";
      inactive_reason?: string | null;
    },
  ): Promise<void> {
    await patchApi<void>(`${this.base()}/admin/${encodeURIComponent(name)}`, payload);
  }

  /**
   * O pacote inteiro de uma versão, como ela foi publicada.
   *
   * É o que permite abrir a tela de versão nova já com o material que está no
   * catálogo — sem ele, publicar a correção de uma linha exigiria ter a pasta
   * original à mão.
   */
  static async packageOf(
    name: string,
    version?: number,
  ): Promise<{
    folder_name: string;
    files: Array<{ path: string; content: string; encoding: "utf8" | "base64" }>;
  }> {
    const query = version === undefined ? "" : `?version=${version}`;

    return getApi(`${this.base()}/admin/${encodeURIComponent(name)}/package${query}`);
  }

  /**
   * Coloca uma versão no ar. Sem `version`, a mais recente.
   *
   * A mesma chamada devolve o catálogo a uma versão anterior: publicar a v1
   * tendo a v2 no ar é o que se quer quando a nova deu errado.
   */
  static async publishVersion(
    name: string,
    version?: number,
  ): Promise<{ version: number; moved: number; kept: number }> {
    return postApi(
      `${this.base()}/admin/${encodeURIComponent(name)}/publish`,
      version === undefined ? {} : { version },
    );
  }

  /**
   * Tira a skill do ar sem apagar nada — ela volta a ser registro do catálogo.
   *
   * Diferente de inativar: inativar é um julgamento, com motivo, que quem
   * instalou vê na próxima execução. Despublicar é um passo atrás no ciclo —
   * a skill some da loja e as versões continuam todas lá.
   */
  static async unpublish(name: string): Promise<void> {
    await delApi<void>(`${this.base()}/admin/${encodeURIComponent(name)}/publish`);
  }

  /** UM arquivo de UMA versão — é o que a comparação entre versões precisa. */
  static async resource(
    name: string,
    path: string,
    version?: number,
  ): Promise<SkillResourceContent> {
    const versao = version === undefined ? "" : `&version=${version}`;

    return getApi<SkillResourceContent>(
      `${this.base()}/admin/${encodeURIComponent(name)}/resource?path=${encodeURIComponent(path)}${versao}`,
    );
  }
}

/** Uma versão publicada, na leitura administrativa (AB#85634, AC10). */
export interface SkillAdminVersion {
  version: number;
  published_at: string;
  changelog: string | null;
  changelog_i18n: Record<string, string> | null;
  current: boolean;
}
